import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi, useApiQuery } from "@/api/useApi";
import type { GlReport } from "@/api/types";
import { Icon } from "@/components/Icon";
import { money, todayIso } from "@/format";

function monthStart(today: string): string {
  return `${today.slice(0, 8)}01`;
}

/** Blank rather than $0.00, so the amounts that matter stand out, as on the spreadsheet. */
function amount(value: number): string {
  return value === 0 ? "" : money(value);
}

/**
 * Reimbursed expenses for a date range, for the general ledger: by department and GL column,
 * with department and grand totals. The same report downloads as Excel.
 */
export function GlReportPage() {
  const api = useApi();
  const today = todayIso();
  const [from, setFrom] = useState(monthStart(today));
  const [to, setTo] = useState(today);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string>();
  const query = `from=${from}&to=${to}`;
  const { data, error, loading } = useApiQuery<GlReport>(
    from && to && from <= to ? `/api/expenses/reports/gl?${query}` : null,
  );

  function download() {
    setDownloading(true);
    setDownloadError(undefined);
    api
      .blob(`/api/expenses/reports/gl.xlsx?${query}`)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `GL Expense Report ${from} to ${to}.xlsx`;
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
      })
      .catch(() => setDownloadError("The Excel file couldn't be made. Try again."))
      .finally(() => setDownloading(false));
  }

  return (
    <div>
      <Link className="back" to="/payroll">
        <Icon name="chevronLeft" size={18} /> Payroll
      </Link>
      <div className="page-head">
        <h1>GL report</h1>
        <button
          type="button"
          className="btn small"
          onClick={download}
          disabled={downloading || !data || data.departments.length === 0}
        >
          <Icon name="download" size={18} /> {downloading ? "Making…" : "Download Excel"}
        </button>
      </div>
      <p className="muted lead">
        Expenses reimbursed between these dates, by department and GL column. Only people with
        expenses are listed.
      </p>

      <div className="card stack gl-range">
        <div className="split">
          <label>
            Reimbursed from
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            To
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
        {from > to && <p className="muted small">The end date is before the start date.</p>}
      </div>

      {downloadError && (
        <div className="error" role="alert">
          {downloadError}
        </div>
      )}
      {error && (
        <div className="error" role="alert">
          {error.message}
        </div>
      )}
      {loading && !data && <div className="card skeleton line wide" />}

      {data && data.departments.length === 0 && (
        <div className="card empty">
          <h2>Nothing reimbursed</h2>
          <p className="muted">No reports were paid with a reimbursement date in this range.</p>
        </div>
      )}

      {data && data.departments.length > 0 && (
        <div className="card gl-table-wrap">
          <table className="gl-table">
            <thead>
              <tr>
                <th>Employee ID</th>
                <th>Name</th>
                {data.columns.map((column) => (
                  <th key={column.name} className="num">
                    {column.name}
                    {column.glCode && <span className="gl-code">{column.glCode}</span>}
                  </th>
                ))}
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.departments.map((department) => (
                <DepartmentRows key={department.name} department={department} />
              ))}
              <tr className="gl-grand">
                <td colSpan={2}>Grand total</td>
                {data.totals.map((total, i) => (
                  <td key={data.columns[i]?.name ?? i} className="num">
                    {amount(total)}
                  </td>
                ))}
                <td className="num">{money(data.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DepartmentRows({ department }: { department: GlReport["departments"][number] }) {
  return (
    <>
      <tr className="gl-department">
        <td colSpan={2 + department.totals.length + 1}>{department.name}</td>
      </tr>
      {department.employees.map((employee) => (
        <tr key={`${employee.employeeId ?? ""}-${employee.name}`}>
          <td>{employee.employeeId ?? ""}</td>
          <td>{employee.name}</td>
          {employee.amounts.map((value, i) => (
            <td key={i} className="num">
              {amount(value)}
            </td>
          ))}
          <td className="num">{money(employee.total)}</td>
        </tr>
      ))}
      <tr className="gl-subtotal">
        <td colSpan={2}>{department.name} total</td>
        {department.totals.map((total, i) => (
          <td key={i} className="num">
            {amount(total)}
          </td>
        ))}
        <td className="num">{money(department.total)}</td>
      </tr>
    </>
  );
}
